import lib from '../../assets/utils/lib';
import { toFacilityProviderRecord } from '../../features/facilities/facilityUtils';
import { renderFacilityExportPdf, PDF_ORG } from './facilityExportPdf';
import { EXCLUDED_FACILITY_EXPORT_FIELD_KEYS } from './facilityExportHelpers';

export const FACILITY_EXPORT_FORMATS = [
  { value: 'csv', label: 'CSV', description: 'Spreadsheet-compatible comma-separated file' },
  { value: 'xlsx', label: 'Excel', description: 'Formatted .xlsx workbook with cover sheet' },
  { value: 'pdf', label: 'PDF', description: 'Branded letterhead report for printing or sharing' },
];

const ORG_NAME = PDF_ORG.name;
const ORG_TAGLINE = PDF_ORG.tagline;
const REPORT_TITLE = PDF_ORG.reportTitle;

function isExcludedExportHeader(label) {
  const normalized = String(label ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  return EXCLUDED_FACILITY_EXPORT_FIELD_KEYS.some(
    (key) => key === normalized || normalized.includes('facility_code'),
  );
}

function dropExcludedExportColumns(headers, matrix) {
  const keepIndexes = headers
    .map((header, index) => ({ header, index }))
    .filter(({ header }) => !isExcludedExportHeader(header))
    .map(({ index }) => index);
  if (keepIndexes.length === headers.length) {
    return { headers, matrix };
  }
  return {
    headers: keepIndexes.map((index) => headers[index]),
    matrix: matrix.map((row) => keepIndexes.map((index) => row[index] ?? '')),
  };
}

/** @param {object} record @param {string} key */
export function getFacilityFieldValue(record, key) {
  if (!record) return '';
  switch (key) {
    case 'name':
      return record.name ?? record.facility_name ?? '';
    case 'city':
      return record.city ?? record.facility_city ?? '';
    case 'state':
      return record.state ?? record.facility_state ?? '';
    case 'status': {
      const s = record.status ?? record.facility_status;
      if (s === 1 || s === '1' || s === 'active') return 'Active';
      if (s === 0 || s === '0' || s === 'inactive') return 'Inactive';
      return s != null ? String(s) : '';
    }
    case 'facility_type':
      return record.facility_type ?? '';
    case 'plan_type':
      return record.plan_type ?? record.facility_plan_type ?? '';
    case 'phone':
      return (
        record.phone ??
        record.facility_phone ??
        record.contact_phone ??
        ''
      );
    case 'email':
      return (
        record.email ??
        record.facility_email ??
        record.contact_email ??
        ''
      );
    case 'address':
      return record.address ?? record.facility_address ?? record.contact_address ?? '';
    case 'website':
      return record.website ?? record.facility_website ?? '';
    case 'facility_id':
      return record.id ?? record.facility_id ?? '';
    default:
      return record[key] != null ? String(record[key]) : '';
  }
}

/**
 * @param {object} params
 * @param {'filtered'|'all'} params.scope
 * @param {string} [params.search]
 * @param {string} [params.state]
 * @param {string|number} [params.status]
 * @param {number} [params.maxRows]
 */
export async function fetchFacilityExportRows({
  scope,
  search,
  state,
  status,
  maxRows = 50000,
}) {
  const rows = [];
  let page = 1;
  const limit = 100;

  while (rows.length < maxRows) {
    const params = { page, limit };
    if (scope === 'filtered') {
      const q = (search ?? '').trim();
      if (q) params.search = q;
    }
    if (state) params.state = state;
    if (status != null && status !== '') params.status = String(status);

    const resp = await lib.getFacilitiesV2(params);
    const body = resp?.data;
    if (body && (Number(body.error) === 1 || body.error === true)) {
      throw new Error(body.message || 'Failed to load facilities for export');
    }
    const batch = Array.isArray(body?.data) ? body.data : [];
    rows.push(...batch);
    const total = body?.pagination?.total ?? rows.length;
    if (!batch.length || batch.length < limit || rows.length >= total) break;
    page += 1;
  }

  if (rows.length >= maxRows) {
    throw new Error(`Export exceeds the maximum of ${maxRows.toLocaleString()} rows. Narrow your filters.`);
  }
  return rows;
}

/**
 * @param {object[]} rows
 * @param {string[]} fieldKeys
 * @param {Array<{ key: string, label: string }>} catalogFields
 */
export function buildExportMatrix(rows, fieldKeys, catalogFields) {
  const labelByKey = Object.fromEntries(catalogFields.map((f) => [f.key, f.label || f.key]));
  const headers = fieldKeys.map((k) => labelByKey[k] || k);
  const matrix = rows.map((row) => {
    const normalized = toFacilityProviderRecord(row);
    return fieldKeys.map((k) => {
      const v = getFacilityFieldValue(normalized, k);
      return v === '' ? getFacilityFieldValue(row, k) : v;
    });
  });
  return { headers, matrix, rowCount: rows.length };
}

/** Parse RFC4180-ish CSV text exported by the backend. */
export function parseExportCsvText(text) {
  const input = String(text ?? '').replace(/^\uFEFF/, '');
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const c = input[i];
    const next = input[i + 1];
    if (inQuotes) {
      if (c === '"' && next === '"') {
        field += '"';
        i += 1;
      } else if (c === '"') {
        inQuotes = false;
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\r' && next === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      i += 1;
    } else if (c === '\n' || c === '\r') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => String(cell ?? '').trim() !== ''));
}

/**
 * Load export matrix from server CSV (same source as CSV download — includes phone, email, etc.).
 * @returns {Promise<{ headers: string[], matrix: string[][], source: 'server'|'client' } | { useAsync: true }>}
 */
export async function resolveFacilityExportMatrix({
  scope,
  search,
  state,
  status,
  fields,
  catalogFields,
  maxRows = 50000,
}) {
  try {
    const fetched = await lib.fetchFacilityExportCsvBlob({
      scope,
      search,
      state,
      status,
      fields,
    });
    if (fetched.useAsync) {
      return { useAsync: true };
    }
    const text = await fetched.blob.text();
    const parsed = parseExportCsvText(text);
    if (parsed.length >= 2) {
      const [headerRow, ...dataRows] = parsed;
      const headers = headerRow.map((h) => String(h ?? ''));
      const matrix = dataRows.map((r) => r.map((c) => String(c ?? '')));
      return {
        ...dropExcludedExportColumns(headers, matrix),
        source: 'server',
      };
    }
  } catch {
    /* fall back to list API */
  }

  const rows = await fetchFacilityExportRows({ scope, search, state, status, maxRows });
  const built = buildExportMatrix(rows, fields, catalogFields);
  return { ...built, source: 'client' };
}

function timestampSlug() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

function triggerBlobDownload(blob, filename) {
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
}

function escapeCsvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function downloadFacilityCsv({ headers, matrix, filename }) {
  const name = filename || `facilities_export_${timestampSlug()}.csv`;
  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...matrix.map((row) => row.map(escapeCsvCell).join(',')),
  ];
  const bom = '\uFEFF';
  const blob = new Blob([bom + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  triggerBlobDownload(blob, name);
  return { success: true, filename: name };
}

export async function downloadFacilityXlsx({ headers, matrix, meta = {}, filename }) {
  const XLSX = await import('xlsx');
  const name = filename || `facilities_export_${timestampSlug()}.xlsx`;
  const generatedAt = meta.generatedAt || new Date().toLocaleString();
  const scopeLabel =
    meta.scope === 'all'
      ? 'All facilities in directory'
      : meta.search
        ? `Matching "${meta.search}"`
        : 'Filtered directory listing';

  const coverRows = [
    [ORG_NAME],
    [ORG_TAGLINE],
    [],
    [REPORT_TITLE],
    ['Provider network listing'],
    [],
    ['Generated', generatedAt],
    ['Scope', scopeLabel],
    ['Total records', matrix.length],
    ['Columns exported', headers.length],
    ['Exported by', meta.exportedBy || '—'],
    [],
    ['Confidential — For authorized internal use only.'],
  ];

  const coverSheet = XLSX.utils.aoa_to_sheet(coverRows);
  coverSheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: 5 } },
  ];
  coverSheet['!cols'] = [{ wch: 18 }, { wch: 48 }];

  const dataSheet = XLSX.utils.aoa_to_sheet([headers, ...matrix]);
  dataSheet['!cols'] = headers.map((h) => ({
    wch: Math.min(40, Math.max(12, String(h).length + 4)),
  }));
  if (matrix.length) {
    const colWidths = headers.map((_, colIdx) => {
      const maxLen = Math.max(
        String(headers[colIdx]).length,
        ...matrix.slice(0, 200).map((row) => String(row[colIdx] ?? '').length),
      );
      return { wch: Math.min(44, Math.max(10, maxLen + 2)) };
    });
    dataSheet['!cols'] = colWidths;
  }
  dataSheet['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft' };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, coverSheet, 'Cover');
  XLSX.utils.book_append_sheet(wb, dataSheet, 'Facilities');
  XLSX.writeFile(wb, name);
  return { success: true, filename: name };
}

export async function downloadFacilityPdf({ headers, matrix, meta = {}, filename }) {
  return renderFacilityExportPdf({ headers, matrix, meta, filename });
}

/**
 * Fetch rows and download in the chosen format (client-side).
 * @param {object} options
 * @param {'csv'|'xlsx'|'pdf'} options.format
 */
export async function generateAndDownloadFacilityExport({
  format,
  scope,
  search,
  state,
  status,
  fields,
  catalogFields,
  meta = {},
  maxRows = 50000,
}) {
  const resolved = await resolveFacilityExportMatrix({
    scope,
    search,
    state,
    status,
    fields,
    catalogFields,
    maxRows,
  });

  if (resolved.useAsync) {
    return { useAsync: true };
  }

  const { headers, matrix } = resolved;
  const exportMeta = {
    ...meta,
    scope,
    search: scope === 'filtered' ? (search ?? '').trim() : undefined,
    rowCount: matrix.length,
    generatedAt: new Date().toLocaleString(),
  };

  switch (format) {
    case 'pdf':
      return downloadFacilityPdf({ headers, matrix, meta: exportMeta });
    case 'xlsx':
      return downloadFacilityXlsx({ headers, matrix, meta: exportMeta });
    case 'csv':
    default:
      return downloadFacilityCsv({ headers, matrix });
  }
}
